import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  AdminButton,
  AdminCard,
  AdminCardHeader,
  AdminEmptyState,
  AdminErrorBanner,
  AdminInfoRow,
  AdminLoadingState,
  AdminMetricCard,
  AdminPageLayout,
  AdminSelect,
  AdminStatusBadge,
  AdminTable,
  AdminTd,
  AdminTextarea,
  AdminTextInput,
  AdminTr,
} from "@/components/admin";
import { disconnectAdminFamilyConnection, fetchAdminFamilyDisconnectPreview, fetchAdminSeniorFamilyConnectionDetail, type AdminMutationImpactPreview } from "@/lib/api";
import type { AdminSeniorFamilyConnectionDetail } from "@/types";
import { formatDateTime } from "@/lib/format";

const connectionStatusLabels: Record<string, string> = {
  ACTIVE: "연결 중",
  DISCONNECTED: "연결 해제",
};

const accountStatusLabels: Record<string, string> = {
  ACTIVE: "활성",
  INACTIVE: "비활성",
  DELETED: "삭제",
};

const formatRelationshipLabel = (value: string | null | undefined) => {
  if (!value || /^[?\s�]+$/.test(value)) return "-";
  return value;
};

const FamilyConnectionDetailPage = () => {
  const navigate = useNavigate();
  const { seniorUserAccountId: seniorUserAccountIdParam } = useParams<{ seniorUserAccountId: string }>();
  const seniorUserAccountId = Number(seniorUserAccountIdParam);
  const [detail, setDetail] = useState<AdminSeniorFamilyConnectionDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [preview, setPreview] = useState<AdminMutationImpactPreview | null>(null);
  const [reasonCode, setReasonCode] = useState("OPERATOR_REQUEST");
  const [reason, setReason] = useState("");
  const [confirmationText, setConfirmationText] = useState("");
  const [saving, setSaving] = useState(false);
  const [auditMessage, setAuditMessage] = useState<string | null>(null);

  const loadDetail = useCallback(async () => {
    if (!Number.isFinite(seniorUserAccountId)) {
      setDetail(null);
      setErrorMessage("잘못된 Senior 계정 ID입니다.");
      setLoading(false);
      return;
    }
    setLoading(true);
    setErrorMessage(null);
    try {
      setDetail(await fetchAdminSeniorFamilyConnectionDetail(seniorUserAccountId));
    } catch (error) {
      setDetail(null);
      setErrorMessage(error instanceof Error ? error.message : "Senior 연결 상세를 불러오지 못했습니다.");
    } finally {
      setLoading(false);
    }
  }, [seniorUserAccountId]);

  useEffect(() => {
    void loadDetail();
  }, [loadDetail]);

  const disconnectedConnectionCount = useMemo(
    () => detail?.connections.filter((connection) => connection.connectionStatus === "DISCONNECTED").length ?? 0,
    [detail],
  );

  const handleDisconnect = async () => {
    if (!preview || !preview.executable || reason.trim().length < 10 || confirmationText.trim() !== preview.targetId) return;
    setSaving(true);
    setErrorMessage(null);
    setAuditMessage(null);
    try {
      const result = await disconnectAdminFamilyConnection(Number(preview.targetId), {
        reasonCode,
        reason: reason.trim(),
        confirmationText: confirmationText.trim(),
      });
      setAuditMessage(`해제 감사 #${result.audit.auditLogId} · ${result.audit.performedAt} · ${result.audit.requestId}`);
      setPreview(null);
      setReason("");
      setConfirmationText("");
      await loadDetail();
    } catch (error) {
      setErrorMessage(error instanceof Error ? error.message : "직접 연결을 해제하지 못했습니다.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <AdminPageLayout
      title="가족 연결 상세"
      subtitle={detail ? `${detail.senior.displayName} Senior의 연결 계정을 확인합니다.` : "Senior별 연결 계정 상세"}
      actions={
        <AdminButton
          type="button"
          variant="secondary"
          size="sm"
          icon="ri-arrow-left-s-line"
          onClick={() => navigate("/admin/family-connections")}
        >
          목록으로
        </AdminButton>
      }
    >
      <div className="mt-4">
        <AdminErrorBanner message={errorMessage} />
      </div>

      {loading ? (
        <AdminLoadingState message="Senior 연결 상세를 불러오는 중입니다." />
      ) : !detail ? (
        <AdminEmptyState title="연결 상세를 표시할 수 없습니다." description="목록으로 돌아가 다른 Senior를 선택해 주세요." />
      ) : (
        <>
          <section className="mb-4 mt-4 grid grid-cols-1 gap-3 md:grid-cols-3">
            <AdminMetricCard title="활성 연결" value={`${detail.activeConnectionCount}명`} icon="ri-link" tone="teal" />
            <AdminMetricCard title="해제 이력" value={`${disconnectedConnectionCount}건`} icon="ri-link-unlink" tone="slate" />
            <AdminMetricCard title="전체 연결 이력" value={`${detail.connections.length}건`} icon="ri-history-line" tone="blue" />
          </section>

          <AdminCard className="mb-4 overflow-hidden">
            <AdminCardHeader icon="ri-user-heart-line" title="Senior 정보" />
            <div className="grid grid-cols-1 lg:grid-cols-[1fr_auto]">
              <div className="grid grid-cols-1 sm:grid-cols-2">
                <AdminInfoRow label="Senior" value={detail.senior.displayName} bordered />
                <AdminInfoRow
                  label="Senior 계정"
                  value={`#${detail.senior.userAccountId} · ${detail.senior.loginId}`}
                  bordered
                />
                <AdminInfoRow
                  label="계정 상태"
                  value={accountStatusLabels[detail.accountStatus] ?? detail.accountStatus}
                  bordered
                />
                <AdminInfoRow label="활성 Keeper" value={`${detail.activeConnectionCount}명`} bordered />
              </div>
              <div className="flex min-w-56 items-center gap-2 border-t border-slate-100 p-4 lg:border-l lg:border-t-0">
                <AdminButton
                  variant="secondary"
                  size="sm"
                  onClick={() => navigate(`/admin/users/seniors/${detail.senior.userAccountId}`)}
                >
                  Senior 상세
                </AdminButton>
                <AdminButton
                  variant="soft"
                  size="sm"
                  onClick={() => navigate(`/admin/users/seniors/${detail.senior.userAccountId}?tab=care`)}
                >
                  생활신호
                </AdminButton>
              </div>
            </div>
          </AdminCard>

          <AdminCard className="overflow-hidden">
            <AdminCardHeader
              icon="ri-team-line"
              title="Keeper 직접 연결 이력 · 활성 및 해제"
            />
            <AdminTable
              headers={["Keeper", "관계", "상태", "연결일", "해제일", "관리"]}
              emptyMessage={detail.connections.length === 0 ? "Keeper 직접 연결 이력이 없습니다." : undefined}
            >
              {detail.connections.map((connection) => (
                <AdminTr key={connection.connectionId}>
                  <AdminTd>
                    <div className="font-semibold text-slate-700">{connection.keeper.displayName}</div>
                    <div className="mt-0.5 text-xs text-slate-400">
                      #{connection.keeper.userAccountId} · {connection.keeper.loginId}
                    </div>
                  </AdminTd>
                  <AdminTd>{formatRelationshipLabel(connection.relationshipLabel)}</AdminTd>
                  <AdminTd>
                    <AdminStatusBadge
                      label={connectionStatusLabels[connection.connectionStatus] ?? connection.connectionStatus}
                      tone={connection.connectionStatus === "ACTIVE" ? "success" : "slate"}
                    />
                  </AdminTd>
                  <AdminTd>{formatDateTime(connection.connectedAt)}</AdminTd>
                  <AdminTd>{formatDateTime(connection.disconnectedAt)}</AdminTd>
                  <AdminTd>
                    {connection.connectionStatus === "ACTIVE" ? (
                      <AdminButton
                        variant="secondary"
                        size="sm"
                        icon="ri-file-search-line"
                        onClick={() => void fetchAdminFamilyDisconnectPreview(connection.connectionId).then((result) => { setPreview(result); setConfirmationText(""); setAuditMessage(null); }).catch((error) => setErrorMessage(error instanceof Error ? error.message : "영향을 조회하지 못했습니다."))}
                      >
                        해제 영향 보기
                      </AdminButton>
                    ) : (
                      <span className="text-xs text-slate-400">해제 완료</span>
                    )}
                  </AdminTd>
                </AdminTr>
              ))}
            </AdminTable>
            {preview && <div className="m-4 space-y-3 rounded-lg border border-amber-200 bg-amber-50 p-3 text-xs text-amber-800"><div><p className="font-semibold">{preview.targetLabel} · 해제 실행 {preview.executable ? "가능" : "차단"}</p>{preview.impacts.map((item) => <p key={item.code}>{item.label}: {item.count}건</p>)}{preview.blockers.map((item) => <p key={item}>차단: {item}</p>)}</div>{preview.executable && <><AdminSelect tone="warning" value={reasonCode} onValueChange={setReasonCode} disabled={saving}><option value="OPERATOR_REQUEST">운영 요청</option><option value="DATA_CORRECTION">데이터 교정</option><option value="SECURITY_RESPONSE">보안 대응</option><option value="TEST_ROLLBACK">테스트·원복 검증</option><option value="OTHER">기타</option></AdminSelect><AdminTextarea tone="warning" value={reason} onChange={(event) => setReason(event.target.value)} disabled={saving} maxLength={500} rows={3} placeholder="해제 사유를 10자 이상 입력하세요."/><AdminTextInput tone="danger" value={confirmationText} onChange={(event) => setConfirmationText(event.target.value)} disabled={saving} placeholder={`연결 ID ${preview.targetId} 입력`}/><AdminButton variant="danger" size="sm" icon="ri-link-unlink" disabled={saving || reason.trim().length < 10 || confirmationText.trim() !== preview.targetId} onClick={() => void handleDisconnect()}>{saving ? "처리 중..." : "직접 연결 해제"}</AdminButton></>}</div>}
            {auditMessage && <p className="m-4 rounded-md border border-emerald-100 bg-emerald-50 px-3 py-2 text-xs text-emerald-700">{auditMessage}</p>}
          </AdminCard>
        </>
      )}
    </AdminPageLayout>
  );
};

export default FamilyConnectionDetailPage;
